import { forwardRef, type InputHTMLAttributes } from 'react';
import { useDensityClass } from '@/design/density';
import { cx } from './cx';
import { useFieldControl } from './Field';

/** Shared by Input, Textarea and Select so all three focus identically. */
export const CONTROL_CLASS =
  'block w-full rounded-md border-0 text-ink-900 shadow-sm ring-1 ring-inset ' +
  'ring-ink-500 placeholder:text-ink-400 focus:ring-2 focus:ring-inset focus:ring-brand-600 ' +
  // The ring eases in rather than snapping: this is the one control the whole
  // page exists to get a candidate into, and the transition is what makes a
  // tap feel answered on a phone that takes a moment to raise the keyboard.
  'transition-[box-shadow,border-color] duration-150 ' +
  'disabled:bg-ink-50 disabled:text-ink-500 aria-[invalid=true]:ring-red-500';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    const field = useFieldControl();
    const size = useDensityClass({ public: 'px-3 py-2.5 text-sm', app: 'px-2.5 py-1.5 text-xs' });
    // `rest` after `field`: an explicit id or aria on the call site wins.
    return <input ref={ref} {...field} {...rest} className={cx(CONTROL_CLASS, size, className)} />;
  },
);
